import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum AeoOnboardingRunStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}
export const AeoOnboardingRunStatusSchema = createEnum(AeoOnboardingRunStatus);

export enum AeoOnboardingStepKey {
  GENERATE_BRAND_IDENTITY_GUIDE = "generate_brand_identity_guide",
  GENERATE_PROMPTS = "generate_prompts",
  SUBMIT_BRIGHTDATA = "submit_brightdata",
  INGEST_BRIGHTDATA = "ingest_brightdata",
  DISCOVER_COMPETITORS = "discover_competitors",
  EXTRACT_MENTIONS = "extract_mentions",
}
export const AeoOnboardingStepKeySchema = createEnum(AeoOnboardingStepKey);

export enum AeoOnboardingStepStatus {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed",
}
export const AeoOnboardingStepStatusSchema = createEnum(
  AeoOnboardingStepStatus,
);

export enum AeoOnboardingErrorCode {
  AEO_NOT_ENABLED = "AEO_NOT_ENABLED",
  BIG_GENERATION_FAILED = "BIG_GENERATION_FAILED",
  PROMPT_GENERATION_FAILED = "PROMPT_GENERATION_FAILED",
  BRIGHTDATA_SUBMIT_FAILED = "BRIGHTDATA_SUBMIT_FAILED",
  BRIGHTDATA_SUBMIT_PARTIAL = "BRIGHTDATA_SUBMIT_PARTIAL",
  BRIGHTDATA_POLL_TIMEOUT = "BRIGHTDATA_POLL_TIMEOUT",
  BRIGHTDATA_INGEST_STALLED = "BRIGHTDATA_INGEST_STALLED",
  BRIGHTDATA_INGEST_TIMEOUT = "BRIGHTDATA_INGEST_TIMEOUT",
  BRIGHTDATA_RUN_FAILED = "BRIGHTDATA_RUN_FAILED",
  COMPETITOR_DISCOVERY_FAILED = "COMPETITOR_DISCOVERY_FAILED",
  MENTION_EXTRACTION_FAILED = "MENTION_EXTRACTION_FAILED",
  TEMPORAL_START_FAILED = "TEMPORAL_START_FAILED",
  INTERNAL_ERROR = "INTERNAL_ERROR",
}
export const AeoOnboardingErrorCodeSchema = createEnum(AeoOnboardingErrorCode);

export const AEO_ONBOARDING_STEPS = [
  {
    key: AeoOnboardingStepKey.GENERATE_BRAND_IDENTITY_GUIDE,
    title: "Generate brand identity guide",
    merchantLabel: "Learning about your brand",
    merchantDescription:
      "We're researching your brand identity, market position, and competitive landscape.",
    order: 1,
  },
  {
    key: AeoOnboardingStepKey.GENERATE_PROMPTS,
    title: "Generate market research prompts",
    merchantLabel: "Crafting research questions",
    merchantDescription:
      "We're generating the questions your customers are asking AI about your market.",
    order: 2,
  },
  {
    key: AeoOnboardingStepKey.SUBMIT_BRIGHTDATA,
    title: "Submit prompts to AI providers",
    merchantLabel: "Querying AI engines",
    merchantDescription:
      "We're asking ChatGPT, Gemini, Perplexity, Grok, and Google AI Mode about your market.",
    order: 3,
  },
  {
    key: AeoOnboardingStepKey.INGEST_BRIGHTDATA,
    title: "Ingest AI responses",
    merchantLabel: "Collecting responses",
    merchantDescription:
      "AI providers are responding. We're collecting and organizing the results.",
    order: 4,
  },
  {
    key: AeoOnboardingStepKey.DISCOVER_COMPETITORS,
    title: "Discover competitors",
    merchantLabel: "Mapping your competitive landscape",
    merchantDescription:
      "We're analyzing AI responses to discover which competitors appear alongside your brand.",
    order: 5,
  },
  {
    key: AeoOnboardingStepKey.EXTRACT_MENTIONS,
    title: "Extract brand & competitor mentions",
    merchantLabel: "Building your insights",
    merchantDescription:
      "We're extracting every mention of your brand and competitors to build your visibility dashboard.",
    order: 6,
  },
] as const;

const AeoOnboardingRunSchema_ = z.object({
  id: z.string(),
  accountId: z.string(),
  status: AeoOnboardingRunStatusSchema,
  startedAt: z.date(),
  completedAt: z.date().nullable(),
  updatedAt: z.date(),
  workflowId: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
});
export const AeoOnboardingRunSchema: z.ZodType<AeoOnboardingRun> =
  AeoOnboardingRunSchema_;
export interface AeoOnboardingRun
  extends z.infer<typeof AeoOnboardingRunSchema_> {}

const AeoOnboardingStepSchema_ = z.object({
  id: z.string(),
  runId: z.string(),
  stepKey: AeoOnboardingStepKeySchema,
  title: z.string(),
  status: AeoOnboardingStepStatusSchema,
  stepOrder: z.number(),
  startedAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  updatedAt: z.date(),
  attemptCount: z.number(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});
export const AeoOnboardingStepSchema: z.ZodType<AeoOnboardingStep> =
  AeoOnboardingStepSchema_;
export interface AeoOnboardingStep
  extends z.infer<typeof AeoOnboardingStepSchema_> {}
